const breakpointsList = {
  breakpoints: ["mobile", "tablet", "desktop", "widescreen"],
  mobile: ["mobile"],
  tablet: ["tablet", "desktop", "widescreen"],
  desktop: ["desktop", "widescreen"],
  widescreen: ["widescreen"],
  tabletDown: ["mobile", "tablet"],
  desktopDown: ["mobile", "tablet", "desktop"]
}

/**
 * Gets the current breakpoint
 */
const getBreakpoint = () => {
  return window.getComputedStyle(document.body, ':before').content.replace(/\"/g, '');;
}

/**
 * Checks if we got out of a group of breakpoints
 * if both old and new breakpoint are part of the group it means it didn't change.
 * @param {String} breakpoints - list of breakpoints as a string
 * @param {*} oldBreakpoint - old break point
 * @param {*} newBreakpoint - new break point
 */
const breakpointChanged = (breakpoints, oldBreakpoint, newBreakpoint) => {
  const breakpointList = breakpointsList.breakpoints;

  const outsideGroup = breakpointList.filter(item => !breakpoints.includes(item));

  return !(breakpoints.includes(oldBreakpoint) && breakpoints.includes(newBreakpoint)) && 
  !(outsideGroup.includes(oldBreakpoint) && outsideGroup.includes(newBreakpoint));
}


/**
 * Excute calbacks when breakpoints are entered and left.
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {String} breakpoints - list of breakpoints as a string
 * @param {Function} callbackIn - triggered when breakpoints is entered
 * @param {Function} callbackOut - triggered when breakpoints is left
 */
const createBreakpointEvent = (breakpoints, callbackIn, callbackOut) => {
  const eventFunction = (e) => {
    const { oldBreakpoint, newBreakpoint } = e.detail;
    if(breakpointChanged(breakpoints, oldBreakpoint, newBreakpoint)) {
      //if we entered the breakpoint
      if(breakpoints.includes(newBreakpoint)){
        if(callbackIn) {
          callbackIn();
        }
      }
      else{
        if(callbackOut) {
          callbackOut();
        }
      }
    }
  }

  if(breakpoints.includes(getBreakpoint())) {
    callbackIn();
  }

  document.addEventListener('breakpoint', eventFunction);

  return {
    destroy: () => {
      document.removeEventListener('breakpoint', eventFunction);
    }
  }
}

/**
 * Excute calbacks when mobile is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onMobile = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.mobile;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when tablet is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onTablet = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.tablet;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when desktop is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onDesktop = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.desktop;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when widescreen is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onWidescreen = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.widescreen;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when breakpoint tablet and lower is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onTabletDown = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.tabletDown;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Excute calbacks when breakpoint desktop and lower is entered or left
 * 
 * Returns an object with the function "destroy" to remove the event listener.
 * 
 * @param {Function} callbackIn - triggered when breakpoint is entered
 * @param {Function} callbackOut - triggered when breakpoint is left
 */
const onDesktopDown = (callbackIn, callbackOut) => {
  const breakpoints = breakpointsList.desktopDown;
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * 
 * @param {String} breakpoint - can be "mobile", "tablet", "desktop", "widescreen", "tabletDown", "desktopDown"
 * @param {*} callbackIn - triggered when breakpoint is entered
 * @param {*} callbackOut - triggered when breakpoint is left
 */
const onBreakpoint = (breakpoint, callbackIn, callbackOut) => {
  const breakpoints = breakpointsList[breakpoint];
  
  return createBreakpointEvent(breakpoints, callbackIn, callbackOut);
}

/**
 * Returns true if in current breakpoint group
 */
const isMobile = () => {
  const breakpoints = breakpointsList.mobile;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Returns true if in current breakpoint group
 */
const isTablet = () => {
  const breakpoints = breakpointsList.tablet;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Returns true if in current breakpoint group
 */
const isDesktop = () => {
  const breakpoints = breakpointsList.desktop;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Returns true if in current breakpoint group
 */
const isWidescreen = () => {
  const breakpoints = breakpointsList.widescreen;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Returns true if in current breakpoint group
 */
const isTabletDown = () => {
  const breakpoints = breakpointsList.tabletDown;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Returns true if in current breakpoint group
 */
const isDesktopDown = () => {
  const breakpoints = breakpoints.desktopDown;
  const currentBreakpoint = getBreakpoint();

  return breakpoints.includes(currentBreakpoint);
}

/**
 * Create an event listener on resize. 
 * When the breakpoint change it dispatch an event with details 
 * about the old breakpoint and the new breakpoint
 */
const initiateBreakpoinListener = () => {

  let breakpoint = getBreakpoint();
  window.addEventListener('resize', () => {
    const currentBreakpoint = getBreakpoint();

    if(breakpoint !== currentBreakpoint){
      var event = new CustomEvent("breakpoint", {
        detail: { 
          oldBreakpoint: breakpoint,
          newBreakpoint: currentBreakpoint
        }
      });
      document.dispatchEvent(event);
    }

    breakpoint = currentBreakpoint;
  });
}

export {
  getBreakpoint,
  initiateBreakpoinListener,
  onMobile,
  onTablet,
  onDesktop,
  onWidescreen,
  onTabletDown,
  onDesktopDown,
  onBreakpoint,
  isMobile,
  isTablet,
  isDesktop,
  isWidescreen,
  isTabletDown,
  isDesktopDown
}